import { useCallback, useState } from "react";

const SK = "test_sk_kdZ81Qsot6uVvTXGkXhrJa2ip9wSvDv3jeyhbN32";
const BASE = "https://pay.chargily.net/test/api/v2";

async function apiCall(method, path, body) {
  const opts = {
    method,
    headers: {
      Authorization: "Bearer " + SK,
      "Content-Type": "application/json",
    },
  };
  if (body) opts.body = JSON.stringify(body);
  const r = await fetch(BASE + path, opts);
  const data = await r.json();
  return { data, ok: r.ok };
}

function ResultBox({ data, error }) {
  if (!data) return null;
  return (
    <pre style={{
      marginTop: 14, padding: "12px 14px", borderRadius: 8,
      fontSize: 12, fontFamily: "monospace",
      background: error ? "#fff0f0" : "#f0faf6",
      border: `1px solid ${error ? "#ffb3b3" : "#a3d9c0"}`,
      color: error ? "#c0392b" : "#0f6e56",
      maxHeight: 300, overflowY: "auto",
      whiteSpace: "pre-wrap", wordBreak: "break-all",
    }}>
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function Card({ children }) {
  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb",
      borderRadius: 14, padding: "20px 22px", marginBottom: 16,
    }}>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 13, color: "#6b7280", marginBottom: 4, fontWeight: 500 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Input({ value, onChange, type = "text" }) {
  return (
    <input
      type={type} value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%", padding: "8px 12px", fontSize: 14,
        border: "1px solid #d1d5db", borderRadius: 8,
        outline: "none", color: "#111827", background: "#fafafa",
        boxSizing: "border-box",
      }}
    />
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value} onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%", padding: "8px 12px", fontSize: 14,
        border: "1px solid #d1d5db", borderRadius: 8,
        outline: "none", color: "#111827", background: "#fafafa",
        boxSizing: "border-box",
      }}
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Btn({ onClick, primary, children, disabled }) {
  return (
    <button
      onClick={onClick} disabled={disabled}
      style={{
        padding: "9px 20px", borderRadius: 8, fontSize: 14, fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        border: primary ? "none" : "1px solid #d1d5db",
        background: primary ? "#1D9E75" : "#fff",
        color: primary ? "#fff" : "#374151",
        opacity: disabled ? 0.6 : 1,
        marginRight: 8, marginBottom: 4,
      }}
    >
      {children}
    </button>
  );
}

function Loader() {
  return <p style={{ fontSize: 13, color: "#6b7280", marginTop: 10 }}>⏳ Chargement...</p>;
}

function BalanceTab() {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [wallets, setWallets] = useState(null);

  const run = useCallback(async () => {
    setLoading(true); setResult(null);
    try {
      const { data, ok } = await apiCall("GET", "/balance");
      setResult(data); setError(!ok);
      if (ok && data.wallets) setWallets(data.wallets);
    } catch (e) { setResult({ error: e.message }); setError(true); }
    setLoading(false);
  }, []);

  return (
    <Card>
      <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 16 }}>Récupère la balance du compte de test.</p>
      {wallets && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          {wallets.map((w, i) => (
            <div key={i} style={{ background: "#f0faf6", borderRadius: 10, padding: "12px 16px" }}>
              <div style={{ fontSize: 12, color: "#0f6e56", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                {w.payment_method || w.currency}
              </div>
              <div style={{ fontSize: 22, fontWeight: 600, color: "#111827" }}>
                {((w.balance || 0) / 100).toFixed(2)}{" "}
                <span style={{ fontSize: 13, fontWeight: 400, color: "#6b7280" }}>DZD</span>
              </div>
            </div>
          ))}
        </div>
      )}
      <Btn primary onClick={run} disabled={loading}>Récupérer la balance</Btn>
      {loading && <Loader />}
      <ResultBox data={result} error={error} />
    </Card>
  );
}

function CustomerTab() {
  const [name, setName] = useState("Ahmed Test");
  const [email, setEmail] = useState("ahmed@test.dz");
  const [phone, setPhone] = useState("0555000000");
  const [city, setCity] = useState("Oran");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    setLoading(true); setResult(null);
    try {
      const { data, ok } = await apiCall("POST", "/customers", {
        name, email, phone, address: { country: "DZ", state: city },
      });
      setResult(data); setError(!ok);
    } catch (e) { setResult({ error: e.message }); setError(true); }
    setLoading(false);
  }, [name, email, phone, city]);

  return (
    <Card>
      <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 16 }}>Créer un nouveau client de test.</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <Field label="Nom"><Input value={name} onChange={setName} /></Field>
        <Field label="Email"><Input value={email} onChange={setEmail} type="email" /></Field>
        <Field label="Téléphone"><Input value={phone} onChange={setPhone} /></Field>
        <Field label="Ville"><Input value={city} onChange={setCity} /></Field>
      </div>
      <Btn primary onClick={run} disabled={loading}>Créer le client</Btn>
      {loading && <Loader />}
      <ResultBox data={result} error={error} />
    </Card>
  );
}

function ProductTab() {
  const [name, setName] = useState("Produit Test");
  const [desc, setDesc] = useState("Un produit de test");
  const [price, setPrice] = useState("1000");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    setLoading(true); setResult(null);
    try {
      const { data: prod, ok: ok1 } = await apiCall("POST", "/products", { name, description: desc });
      if (!ok1) { setResult(prod); setError(true); setLoading(false); return; }
      const { data: priceData, ok: ok2 } = await apiCall("POST", "/prices", {
        amount: parseInt(price) * 100, currency: "dzd", product_id: prod.id,
      });
      setResult({ product: prod, price: priceData }); setError(!ok2);
    } catch (e) { setResult({ error: e.message }); setError(true); }
    setLoading(false);
  }, [name, desc, price]);

  return (
    <Card>
      <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 16 }}>Créer un produit avec son prix.</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <Field label="Nom du produit"><Input value={name} onChange={setName} /></Field>
        <Field label="Description"><Input value={desc} onChange={setDesc} /></Field>
        <Field label="Prix (DZD)"><Input value={price} onChange={setPrice} type="number" /></Field>
        <Field label="Devise">
          <Select value="dzd" onChange={() => {}} options={[{ value: "dzd", label: "DZD — Dinar algérien" }]} />
        </Field>
      </div>
      <Btn primary onClick={run} disabled={loading}>Créer produit + prix</Btn>
      {loading && <Loader />}
      <ResultBox data={result} error={error} />
    </Card>
  );
}

function CheckoutTab() {
  const [amount, setAmount] = useState("2000");
  const [method, setMethod] = useState("");
  const [successUrl, setSuccessUrl] = useState("https://example.com/success");
  const [failureUrl, setFailureUrl] = useState("https://example.com/failure");
  const [descVal, setDescVal] = useState("Paiement de test");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  const run = useCallback(async () => {
    setLoading(true); setResult(null); setCheckoutUrl(null);
    const body = {
      amount: parseInt(amount) * 100, currency: "dzd",
      success_url: successUrl, failure_url: failureUrl, description: descVal,
    };
    if (method) body.payment_method = method;
    try {
      const { data, ok } = await apiCall("POST", "/checkouts", body);
      setResult(data); setError(!ok);
      if (ok && data.checkout_url) setCheckoutUrl(data.checkout_url);
    } catch (e) { setResult({ error: e.message }); setError(true); }
    setLoading(false);
  }, [amount, method, successUrl, failureUrl, descVal]);

  return (
    <Card>
      <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 16 }}>Créer un checkout de paiement.</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <Field label="Montant (DZD)"><Input value={amount} onChange={setAmount} type="number" /></Field>
        <Field label="Méthode de paiement">
          <Select value={method} onChange={setMethod} options={[
            { value: "", label: "Les deux" },
            { value: "edahabia", label: "EDAHABIA" },
            { value: "cib", label: "CIB" },
          ]} />
        </Field>
        <Field label="URL succès"><Input value={successUrl} onChange={setSuccessUrl} /></Field>
        <Field label="URL échec"><Input value={failureUrl} onChange={setFailureUrl} /></Field>
      </div>
      <Field label="Description"><Input value={descVal} onChange={setDescVal} /></Field>
      <Btn primary onClick={run} disabled={loading}>Créer le checkout</Btn>
      {loading && <Loader />}
      {checkoutUrl && (
        <div style={{
          marginTop: 12, padding: "10px 14px", background: "#f0faf6",
          border: "1px solid #a3d9c0", borderRadius: 8,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 13, color: "#0f6e56" }}>🔗 Lien :</span>
          <a href={checkoutUrl} target="_blank" rel="noreferrer"
            style={{ fontSize: 13, color: "#1D9E75", wordBreak: "break-all", fontWeight: 500 }}>
            {checkoutUrl}
          </a>
        </div>
      )}
      <ResultBox data={result} error={error} />
    </Card>
  );
}

function ListTab() {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);

  const run = useCallback(async (res) => {
    setActive(res); setLoading(true); setResult(null);
    try {
      const { data, ok } = await apiCall("GET", "/" + res);
      setResult(data); setError(!ok);
    } catch (e) { setResult({ error: e.message }); setError(true); }
    setLoading(false);
  }, []);

  const resources = [
    { key: "customers", label: "Clients" },
    { key: "products", label: "Produits" },
    { key: "prices", label: "Prix" },
    { key: "checkouts", label: "Checkouts" },
    { key: "payment-links", label: "Payment Links" },
  ];

  return (
    <Card>
      <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 16 }}>Lister les ressources existantes.</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
        {resources.map((r) => (
          <button key={r.key} onClick={() => run(r.key)} disabled={loading} style={{
            padding: "8px 16px", borderRadius: 8, fontSize: 14,
            cursor: loading ? "not-allowed" : "pointer",
            border: "1px solid",
            borderColor: active === r.key ? "#1D9E75" : "#d1d5db",
            background: active === r.key ? "#f0faf6" : "#fff",
            color: active === r.key ? "#0f6e56" : "#374151",
            fontWeight: active === r.key ? 600 : 400,
          }}>
            {r.label}
          </button>
        ))}
      </div>
      {loading && <Loader />}
      <ResultBox data={result} error={error} />
    </Card>
  );
}

const TABS = ["Balance", "Client", "Produit", "Checkout", "Lister"];

export default function ChargilyDashboard() {
  const [activeTab, setActiveTab] = useState(0);

  const tabComponents = [
    <BalanceTab />,
    <CustomerTab />,
    <ProductTab />,
    <CheckoutTab />,
    <ListTab />,
  ];

  return (
    <div style={{
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      maxWidth: 820, margin: "0 auto",
      padding: "24px 20px", color: "#111827",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 10, background: "#1D9E75",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 700, fontSize: 15,
        }}>CP</div>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Chargily Pay</h1>
          <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>API V2 · Mode test</p>
        </div>
        <span style={{
          marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500,
          background: "#f0faf6", border: "1px solid #a3d9c0", color: "#0f6e56",
        }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#1D9E75" }} />
          Test mode
        </span>
      </div>

      <div style={{ display: "flex", gap: 2, borderBottom: "1px solid #e5e7eb", marginBottom: 20 }}>
        {TABS.map((tab, i) => (
          <button key={tab} onClick={() => setActiveTab(i)} style={{
            padding: "9px 18px", fontSize: 14,
            fontWeight: activeTab === i ? 600 : 400,
            cursor: "pointer", border: "none", background: "none",
            color: activeTab === i ? "#1D9E75" : "#6b7280",
            borderBottom: `2px solid ${activeTab === i ? "#1D9E75" : "transparent"}`,
            marginBottom: -1,
          }}>
            {tab}
          </button>
        ))}
      </div>

      {tabComponents[activeTab]}
    </div>
  );
}